import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Select,
  DatePicker,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Segmented,
  Tag,
  Empty,
  message,
} from 'antd';
import {
  SyncOutlined,
  BookOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { AccountItem, GeneralLedgerResponse, GeneralLedgerAccount, GeneralLedgerMovement } from '../../services/accountingService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 2 }).format(val || 0);

const MONTHS = [
  { value: 0, label: 'Enero' },
  { value: 1, label: 'Febrero' },
  { value: 2, label: 'Marzo' },
  { value: 3, label: 'Abril' },
  { value: 4, label: 'Mayo' },
  { value: 5, label: 'Junio' },
  { value: 6, label: 'Julio' },
  { value: 7, label: 'Agosto' },
  { value: 8, label: 'Septiembre' },
  { value: 9, label: 'Octubre' },
  { value: 10, label: 'Noviembre' },
  { value: 11, label: 'Diciembre' },
];

const GeneralLedgerView: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();

  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const [filterMode, setFilterMode] = useState<'month' | 'range'>('month');
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const [ledgerData, setLedgerData] = useState<GeneralLedgerResponse | null>(null);

  const getDateRange = () => {
    if (filterMode === 'month') {
      const start = dayjs().year(selectedYear).month(selectedMonth).startOf('month').format('YYYY-MM-DD');
      const end = dayjs().year(selectedYear).month(selectedMonth).endOf('month').format('YYYY-MM-DD');
      return { start, end };
    } else {
      return {
        start: customRange[0]?.format('YYYY-MM-DD') || dayjs().startOf('month').format('YYYY-MM-DD'),
        end: customRange[1]?.format('YYYY-MM-DD') || dayjs().endOf('month').format('YYYY-MM-DD'),
      };
    }
  };

  const loadAccounts = async () => {
    if (!company.current.id) return;
    try {
      const res = await securePost('/accounting/accounts', { company_id: company.current.id });
      if (res?.data) {
        setAccounts(res.data);
        if (!selectedAccountId && res.data.length > 0) {
          // Default to first active account with level >= 3
          const defaultAcc = res.data.find((a: AccountItem) => a.level >= 3) || res.data[0];
          setSelectedAccountId(defaultAcc.id);
        }
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadLedger = async () => {
    if (!company.current.id) return;
    try {
      const { start, end } = getDateRange();
      const res = await securePost('/accounting/reports/general-ledger', {
        company_id: company.current.id,
        start_date: start,
        end_date: end,
        account_id: selectedAccountId || undefined,
      });
      if (res?.data) {
        const raw = res.data?.report || res.data;
        setLedgerData(raw);
      }
    } catch (err) {
      console.error('Error loading general ledger:', err);
      message.error('No se pudo generar el Libro Mayor y Auxiliar.');
    }
  };

  useEffect(() => {
    if (company.current.id) {
      loadAccounts();
    }
  }, [company.current.id]);

  useEffect(() => {
    if (company.current.id && selectedAccountId) {
      loadLedger();
    }
  }, [company.current.id, selectedAccountId, filterMode, selectedYear, selectedMonth, customRange]);

  const currentAccountLedger: GeneralLedgerAccount | undefined = ledgerData?.account ? {
    account_id: ledgerData.account.id,
    code: ledgerData.account.code,
    name: ledgerData.account.name,
    nature: ledgerData.account.nature,
    initial_balance: Number(ledgerData.initial_balance || 0),
    total_debit: Number(ledgerData.total_debit || 0),
    total_credit: Number(ledgerData.total_credit || 0),
    final_balance: Number(ledgerData.final_balance || 0),
    movements: ledgerData.movements || [],
  } : undefined;

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'entry_date',
      key: 'entry_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Comprobante',
      dataIndex: 'entry_number',
      key: 'entry_number',
      width: 140,
      render: (num: string) => <Text strong style={{ color: '#1677ff' }}>{num}</Text>,
    },
    {
      title: 'Tercero',
      key: 'contact',
      width: 200,
      render: (_: any, r: GeneralLedgerMovement) => {
        if (!r.contact_name) return <Text type="secondary">—</Text>;
        return (
          <div>
            <Text strong>{r.contact_name}</Text>
            {r.contact_document && <div><Text type="secondary" style={{ fontSize: 11 }}>NIT: {r.contact_document}</Text></div>}
          </div>
        );
      },
    },
    {
      title: 'Concepto / Detalle',
      dataIndex: 'concept',
      key: 'concept',
      ellipsis: true,
      render: (c: string, r: any) => c || r.line_description || '—',
    },
    {
      title: 'Débito',
      dataIndex: 'debit',
      key: 'debit',
      align: 'right' as const,
      width: 140,
      render: (val: number) => (val > 0 ? <Text style={{ color: '#1677ff' }}>{formatCurrency(val)}</Text> : '—'),
    },
    {
      title: 'Crédito',
      dataIndex: 'credit',
      key: 'credit',
      align: 'right' as const,
      width: 140,
      render: (val: number) => (val > 0 ? <Text style={{ color: '#722ed1' }}>{formatCurrency(val)}</Text> : '—'),
    },
    {
      title: 'Saldo Progresivo',
      dataIndex: 'balance',
      key: 'balance',
      align: 'right' as const,
      width: 160,
      render: (val: number) => <Text strong>{formatCurrency(val)}</Text>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Libro Mayor y Auxiliar</Title>
          <Text type="secondary">Movimientos cronológicos detallados y saldos progresivos por cuenta</Text>
        </div>
        <Space wrap>
          <Button icon={<SyncOutlined spin={loading} />} onClick={loadLedger}>
            Refrescar
          </Button>
        </Space>
      </div>

      {/* Filter Bar */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Seleccionar Cuenta Contable:</Text>
            <Select
              placeholder="Buscar por código o nombre..."
              showSearch
              optionFilterProp="children"
              value={selectedAccountId}
              onChange={setSelectedAccountId}
              style={{ width: '100%' }}
            >
              {accounts.map((acc) => (
                <Option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={6}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Período:</Text>
            <Segmented
              value={filterMode}
              onChange={(val) => setFilterMode(val as 'month' | 'range')}
              options={[
                { label: 'Mes/Año', value: 'month' },
                { label: 'Rango', value: 'range' },
              ]}
            />
          </Col>

          <Col xs={24} md={8}>
            {filterMode === 'month' ? (
              <Space wrap style={{ marginTop: 22 }}>
                <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 100 }}>
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <Option key={y} value={y}>{y}</Option>
                  ))}
                </Select>
                <Select value={selectedMonth} onChange={setSelectedMonth} style={{ width: 130 }}>
                  {MONTHS.map((m) => (
                    <Option key={m.value} value={m.value}>{m.label}</Option>
                  ))}
                </Select>
              </Space>
            ) : (
              <div style={{ marginTop: 22 }}>
                <RangePicker
                  value={customRange}
                  onChange={(d) => d && setCustomRange([d[0]!, d[1]!])}
                  format="DD/MM/YYYY"
                />
              </div>
            )}
          </Col>
        </Row>
      </Card>

      {/* Account Balance Summary Cards */}
      {currentAccountLedger && (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fafafa', borderRadius: 8 }}>
              <Statistic
                title={<Text type="secondary">Saldo Inicial Arrastrado</Text>}
                value={currentAccountLedger.initial_balance}
                formatter={(v) => formatCurrency(Number(v))}
              />
              <Tag color={currentAccountLedger.nature === 'DEBIT' ? 'blue' : 'purple'} style={{ marginTop: 6 }}>
                Naturaleza: {currentAccountLedger.nature === 'DEBIT' ? 'DÉBITO' : 'CRÉDITO'}
              </Tag>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#f0f5ff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ color: '#1677ff' }}>Total Débitos Período (+)</Text>}
                value={currentAccountLedger.total_debit}
                formatter={(v) => formatCurrency(Number(v))}
                prefix={<ArrowUpOutlined style={{ color: '#1677ff' }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#f9f0ff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ color: '#722ed1' }}>Total Créditos Período (-)</Text>}
                value={currentAccountLedger.total_credit}
                formatter={(v) => formatCurrency(Number(v))}
                prefix={<ArrowDownOutlined style={{ color: '#722ed1' }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#f6ffed', borderRadius: 8 }}>
              <Statistic
                title={<Text strong style={{ color: '#389e0d' }}>Saldo Final Acumulado</Text>}
                value={currentAccountLedger.final_balance}
                formatter={(v) => formatCurrency(Number(v))}
                valueStyle={{ color: '#237804', fontWeight: 600 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Al corte del período consultado</Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* Movements Table */}
      <Card
        title={
          currentAccountLedger ? (
            <Space>
              <BookOutlined />
              <span>Movimientos de la Cuenta: <strong>{currentAccountLedger.code} - {currentAccountLedger.name}</strong></span>
            </Space>
          ) : (
            'Movimientos'
          )
        }
        bordered={false}
        style={{ borderRadius: 8 }}
      >
        <Table
          dataSource={currentAccountLedger?.movements || []}
          columns={columns}
          rowKey={(r: any) => r.id || `${r.entry_id}-${r.date}-${r.running_balance}`}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                description={
                  currentAccountLedger
                    ? 'Esta cuenta no registra movimientos en el período seleccionado.'
                    : 'Seleccione una cuenta contable para ver su historial de movimientos.'
                }
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default GeneralLedgerView;
